import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import { dashboardAPI } from '../../services/api';
import {
    Package, Tag, Image, ImagePlus, Star,
    Settings, Users, Globe, LogOut, Menu, X, Megaphone, ShoppingCart, FileText, Ticket,
    Wrench, Building2, Search, LayoutGrid, Newspaper, Zap, HelpCircle, Bell, AlertTriangle, UserCheck
} from 'lucide-react';

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Notification state
    const [notif, setNotif] = useState({ pendingOrders: 0, pendingReviews: 0, pendingQuestions: 0, lowStock: 0 });
    const [notifOpen, setNotifOpen] = useState(false);
    const [toasts, setToasts] = useState([]);
    const prevNotifRef = useRef(null);
    const notifPanelRef = useRef(null);

    const totalNotif = notif.pendingOrders + notif.pendingReviews + notif.pendingQuestions + notif.lowStock;

    const addToast = useCallback((message, type = 'info', link = null) => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev.slice(-4), { id, message, type, link }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 6000);
    }, []);

    const fetchNotifications = useCallback(async () => {
        try {
            const res = await dashboardAPI.getNotifications();
            const data = res.data;
            setNotif(data);

            if (prevNotifRef.current) {
                const prev = prevNotifRef.current;
                if (data.pendingOrders > prev.pendingOrders)
                    addToast(`${data.pendingOrders - prev.pendingOrders} yeni sipariş bekliyor`, 'order', '/admin/orders');
                if (data.pendingReviews > prev.pendingReviews)
                    addToast(`${data.pendingReviews - prev.pendingReviews} yeni yorum onay bekliyor`, 'review', '/admin/reviews');
                if (data.pendingQuestions > prev.pendingQuestions)
                    addToast(`${data.pendingQuestions - prev.pendingQuestions} yeni soru cevap bekliyor`, 'question', '/admin/questions');
                if (data.lowStock > prev.lowStock)
                    addToast('Düşük stoklu ürün sayısı arttı', 'stock', '/admin/products');
            }
            prevNotifRef.current = data;
        } catch (e) {
            console.error('Notification fetch error:', e);
        }
    }, [addToast]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 15000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // Close notification panel on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (notifPanelRef.current && !notifPanelRef.current.contains(e.target) && !e.target.closest('.admin-notif-bell')) {
                setNotifOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/admin/login');
    };

    const getBadge = (count) => count > 0 ? <span className="admin-nav-badge">{count}</span> : null;

    const notifItems = [
        { label: 'Bekleyen Sipariş', count: notif.pendingOrders, icon: <ShoppingCart size={16} />, link: '/admin/orders', color: '#3b82f6' },
        { label: 'Onay Bekleyen Yorum', count: notif.pendingReviews, icon: <Star size={16} />, link: '/admin/reviews', color: '#f59e0b' },
        { label: 'Cevaplanmamış Soru', count: notif.pendingQuestions, icon: <HelpCircle size={16} />, link: '/admin/questions', color: '#8b5cf6' },
        { label: 'Düşük / Tükenmiş Stok', count: notif.lowStock, icon: <AlertTriangle size={16} />, link: '/admin/products', color: '#ef4444' },
    ];

    return (
        <div className="admin-layout">
            <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
                <div className="admin-sidebar-brand">
                    <img src="/logo_noora.png" alt="Noora Privé Logo" style={{ width: '168px', height: 'auto', borderRadius: '8px', objectFit: 'contain' }} />
                </div>

                <nav className="admin-nav">
                    <div className="admin-nav-label">Ürün & Satış</div>
                    <NavLink to="/admin/products" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Package size={18} /> Ürünler {getBadge(notif.lowStock)}
                    </NavLink>
                    <NavLink to="/admin/categories" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Tag size={18} /> Kategoriler
                    </NavLink>
                    <NavLink to="/admin/orders" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <ShoppingCart size={18} /> Siparişler {getBadge(notif.pendingOrders)}
                    </NavLink>
                    <NavLink to="/admin/coupons" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Ticket size={18} /> Kuponlar
                    </NavLink>

                    <div className="admin-nav-label">İçerik Yönetimi</div>
                    <NavLink to="/admin/services" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Wrench size={18} /> Hizmetler
                    </NavLink>
                    <NavLink to="/admin/corporate-pages" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Building2 size={18} /> Kurumsal Sayfalar
                    </NavLink>
                    <NavLink to="/admin/legal-pages" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <FileText size={18} /> Hukuki Sayfalar
                    </NavLink>

                    <div className="admin-nav-label">Görsel & Tasarım</div>
                    <NavLink to="/admin/sliders" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Image size={18} /> Slider
                    </NavLink>
                    <NavLink to="/admin/homepage-categories" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <LayoutGrid size={18} /> Anasayfa Kategorileri
                    </NavLink>
                    <NavLink to="/admin/popup-banner" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Zap size={18} /> Popup Reklam
                    </NavLink>
                    <NavLink to="/admin/editorial-banner" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Newspaper size={18} /> Editorial Banner
                    </NavLink>
                    <NavLink to="/admin/banners" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Megaphone size={18} /> Bannerlar
                    </NavLink>
                    <NavLink to="/admin/media-library" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <ImagePlus size={18} /> Görsel Havuzu
                    </NavLink>

                    <div className="admin-nav-label">Geri Bildirim</div>
                    <NavLink to="/admin/reviews" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Star size={18} /> Ürün Yorumları {getBadge(notif.pendingReviews)}
                    </NavLink>
                    <NavLink to="/admin/questions" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <HelpCircle size={18} /> Ürün Soruları {getBadge(notif.pendingQuestions)}
                    </NavLink>

                    <div className="admin-nav-label">Ayarlar</div>
                    <NavLink to="/admin/page-seo" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Search size={18} /> SEO Yönetimi
                    </NavLink>
                    <NavLink to="/admin/site-settings" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Settings size={18} /> Site Ayarları
                    </NavLink>
                    <NavLink to="/admin/users" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <Users size={18} /> Kullanıcılar
                    </NavLink>
                    <NavLink to="/admin/customers" className={({ isActive }) => `admin-nav-item ${isActive ? 'active' : ''}`} onClick={() => setSidebarOpen(false)}>
                        <UserCheck size={18} /> Müşteriler
                    </NavLink>

                    <div className="admin-nav-divider"></div>

                    <a href="/" className="admin-nav-item" target="_blank">
                        <Globe size={18} /> Siteyi Görüntüle
                    </a>

                    <button className="admin-nav-item" onClick={handleLogout}>
                        <LogOut size={18} /> Çıkış Yap
                    </button>
                </nav>
            </aside>

            {/* Overlay when sidebar is open on mobile */}
            {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

            <main className="admin-content">
                {/* Top bar */}
                <div className="admin-topbar">
                    <div className="admin-topbar-left">
                        <button className="mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
                            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                        <img src="/logo_noora.png" alt="Noora Privé" className="admin-mobile-logo" />
                    </div>
                    <div className="admin-topbar-right">
                        <button className="admin-notif-bell" onClick={() => setNotifOpen(!notifOpen)}>
                            <Bell size={20} />
                            {totalNotif > 0 && <span className="admin-notif-count">{totalNotif > 99 ? '99+' : totalNotif}</span>}
                        </button>
                    </div>
                </div>

                {/* Notification panel */}
                {notifOpen && (
                    <div className="admin-notif-panel" ref={notifPanelRef}>
                        <div className="admin-notif-panel-header">
                            <strong>Bildirimler</strong>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{totalNotif} bekleyen</span>
                        </div>
                        {notifItems.map((item, i) => (
                            <Link
                                key={i}
                                to={item.link}
                                className="admin-notif-item"
                                onClick={() => setNotifOpen(false)}
                            >
                                <span className="admin-notif-item-icon" style={{ color: item.color }}>{item.icon}</span>
                                <span className="admin-notif-item-label">{item.label}</span>
                                <span className="admin-notif-item-count" style={{ background: item.count > 0 ? item.color : 'var(--text-muted)' }}>
                                    {item.count}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}

                <Outlet />

                {/* Toast notifications */}
                {toasts.length > 0 && (
                    <div className="admin-toast-container">
                        {toasts.map(toast => (
                            <div
                                key={toast.id}
                                className={`admin-toast admin-toast-${toast.type}`}
                                onClick={() => {
                                    if (toast.link) navigate(toast.link);
                                    setToasts(prev => prev.filter(t => t.id !== toast.id));
                                }}
                            >
                                <span>{toast.message}</span>
                                <div className="admin-toast-progress" />
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
